/**
 * Custom task statuses manager for a project.
 * Supports color picker, terminal flag, comment requirement, and drag-to-reorder.
 */
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, GripVertical, Loader2, MessageSquare, Flag } from 'lucide-react'
import { apiClient } from '../../services/api'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import type { ApiErrorLike } from '../../utils/apiError'

export interface TaskStatusItem {
  id: number
  project_id: number
  name: string
  color: string
  position: number
  is_terminal: boolean
  require_comment_on_enter: boolean
  created_at: string
}

const PRESET_COLORS = [
  '#6B7280', '#3B82F6', '#8B5CF6', '#10B981',
  '#F59E0B', '#EF4444', '#EC4899', '#14B8A6',
]

interface Props {
  projectId: number
}

export function TaskStatusesManager({ projectId }: Props) {
  const [statuses, setStatuses] = useState<TaskStatusItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dragging, setDragging] = useState<number | null>(null)

  const [form, setForm] = useState({
    name: '',
    color: '#6B7280',
    is_terminal: false,
    require_comment_on_enter: false,
  })
  const [formError, setFormError] = useState('')

  useEffect(() => { load() }, [projectId])

  async function load() {
    setLoading(true)
    try {
      const { data } = await apiClient.get<TaskStatusItem[]>(
        `/api/v1/projects/${projectId}/statuses/`,
        { params: { project_id: projectId } }
      )
      setStatuses(data)
    } catch {
      toast.error('Failed to load task statuses')
    } finally {
      setLoading(false)
    }
  }

  function openDialog() {
    setForm({ name: '', color: '#6B7280', is_terminal: false, require_comment_on_enter: false })
    setFormError('')
    setShowDialog(true)
  }

  async function handleCreate() {
    if (!form.name.trim()) { setFormError('Status name is required'); return }
    setSaving(true)
    try {
      const { data } = await apiClient.post<TaskStatusItem>(
        `/api/v1/projects/${projectId}/statuses/`,
        { ...form, name: form.name.trim(), position: statuses.length },
        { params: { project_id: projectId } }
      )
      setStatuses(prev => {
        // If this is terminal, clear previous terminal flag in UI
        const updated = prev.map(s => data.is_terminal ? { ...s, is_terminal: false } : s)
        return [...updated, data]
      })
      setShowDialog(false)
      toast.success('Status created')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to create status')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(statusId: number) {
    try {
      await apiClient.delete(`/api/v1/projects/${projectId}/statuses/${statusId}`, {
        params: { project_id: projectId },
      })
      setStatuses(prev => prev.filter(s => s.id !== statusId))
      toast.success('Status deleted')
    } catch {
      toast.error('Failed to delete status')
    }
  }

  async function handleToggle(status: TaskStatusItem, field: 'is_terminal' | 'require_comment_on_enter') {
    const update = { [field]: !status[field] }
    try {
      const { data } = await apiClient.patch<TaskStatusItem>(
        `/api/v1/projects/${projectId}/statuses/${status.id}`,
        update,
        { params: { project_id: projectId } }
      )
      setStatuses(prev =>
        prev.map(s => {
          if (field === 'is_terminal' && data.is_terminal) {
            return s.id === data.id ? data : { ...s, is_terminal: false }
          }
          return s.id === data.id ? data : s
        })
      )
    } catch {
      toast.error('Failed to update status')
    }
  }

  function handleDragStart(index: number) { setDragging(index) }
  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault()
    if (dragging === null || dragging === index) return
    const reordered = [...statuses]
    const [moved] = reordered.splice(dragging, 1)
    reordered.splice(index, 0, moved)
    setStatuses(reordered)
    setDragging(index)
  }
  async function handleDragEnd() {
    setDragging(null)
    try {
      await apiClient.post(
        `/api/v1/projects/${projectId}/statuses/reorder`,
        { status_ids: statuses.map(s => s.id) },
        { params: { project_id: projectId } }
      )
    } catch {
      toast.error('Failed to save order')
      load()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" aria-label="Loading statuses">
        <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
        <span className="sr-only">Loading task statuses</span>
      </div>
    )
  }

  return (
    <section aria-labelledby="task-statuses-heading">
      <div className="flex items-center justify-between mb-4">
        <h2 id="task-statuses-heading" className="text-base font-semibold">Task Statuses</h2>
        <Button onClick={openDialog} size="sm" className="min-h-[44px] min-w-[44px]">
          <Plus className="size-4 mr-1" aria-hidden />
          Add Status
        </Button>
      </div>

      {statuses.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Using system defaults. Add a status to customize this project.
        </p>
      ) : (
        <ul className="space-y-2" aria-label="Task status list">
          {statuses.map((s, index) => (
            <li
              key={s.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={e => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 rounded-lg border bg-card p-3 ${dragging === index ? 'opacity-50' : ''}`}
            >
              <button
                aria-label="Drag to reorder"
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
                tabIndex={-1}
              >
                <GripVertical className="size-4" aria-hidden />
              </button>

              {/* Color swatch */}
              <span
                className="size-4 rounded-full shrink-0 ring-1 ring-border"
                style={{ backgroundColor: s.color }}
                aria-hidden
              />

              <span className="font-medium text-sm flex-1 min-w-0 truncate">{s.name}</span>

              <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                {s.is_terminal && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-500 gap-1">
                    <Flag className="size-3" aria-hidden /> Terminal
                  </Badge>
                )}
                {s.require_comment_on_enter && (
                  <Badge variant="outline" className="text-xs gap-1">
                    <MessageSquare className="size-3" aria-hidden /> Comment required
                  </Badge>
                )}

                <button
                  onClick={() => handleToggle(s, 'is_terminal')}
                  className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted min-h-[44px] px-1"
                  aria-label={s.is_terminal ? `Unmark ${s.name} as terminal` : `Mark ${s.name} as terminal`}
                >
                  {s.is_terminal ? 'Un-terminal' : 'Terminal'}
                </button>

                <button
                  onClick={() => handleToggle(s, 'require_comment_on_enter')}
                  className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted min-h-[44px] px-1"
                  aria-label={`Toggle comment requirement for ${s.name}`}
                >
                  {s.require_comment_on_enter ? 'No comment' : 'Require comment'}
                </button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 min-h-[44px] min-w-[44px]"
                  onClick={() => handleDelete(s.id)}
                  aria-label={`Delete ${s.name} status`}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="status-name">
                Name <span aria-hidden className="text-destructive">*</span>
              </Label>
              <Input
                id="status-name"
                value={form.name}
                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFormError('') }}
                placeholder="e.g. In Review, Blocked, Deployed"
                className="mt-1 min-h-[44px]"
                aria-required="true"
                aria-invalid={!!formError}
                aria-describedby={formError ? 'status-name-error' : undefined}
              />
              {formError && (
                <p id="status-name-error" className="text-sm text-destructive mt-1" role="alert">{formError}</p>
              )}
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setForm(f => ({ ...f, color }))}
                    className={`size-8 rounded-full ring-offset-2 transition-shadow min-h-[44px] min-w-[44px] ${form.color === color ? 'ring-2 ring-ring' : 'hover:ring-1 hover:ring-border'}`}
                    style={{ backgroundColor: color }}
                    aria-label={`Select color ${color}`}
                    aria-pressed={form.color === color}
                  />
                ))}
                <Input
                  type="color"
                  value={form.color}
                  onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                  className="h-9 w-16 p-1 cursor-pointer"
                  aria-label="Custom color"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is-terminal"
                checked={form.is_terminal}
                onCheckedChange={v => setForm(f => ({ ...f, is_terminal: v }))}
              />
              <Label htmlFor="is-terminal">Terminal — counts as complete in progress calculations</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="require-comment"
                checked={form.require_comment_on_enter}
                onCheckedChange={v => setForm(f => ({ ...f, require_comment_on_enter: v }))}
              />
              <Label htmlFor="require-comment">Require comment when entering this status</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="min-h-[44px]">
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" aria-hidden />}
              Create Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
